import { signIn } from 'next-auth/react'
import { useContext } from 'react'
import { mutate } from 'swr'

import { resetAccessTokenCache } from '@/api/config/axios'
import { E0004 } from '@/constants/messages'
import { TrackedForms } from '@/constants/mixpanel'
import { SnackbarContext } from '@/contexts/Snackbar'
import { createTrackedForm } from '@/hooks/common/Analytics/useFormAnalytics'
import { generateMixpanelUser } from '@/hooks/common/Analytics/useLoginAnalytics'

type SwitchAccountLoginForm = { email: string; password: string }

/**
 * アカウント切替用ログインフック
 * 画面遷移は行わず、現在表示している画面を維持したままアカウントを切り替える
 * @param close ログイン成功時に呼び出すダイアログを閉じる関数
 */
export const useSwitchAccountLogin = (close: () => void) => {
  const formMethods = createTrackedForm<SwitchAccountLoginForm>({
    formName: TrackedForms.Auth.SwitchAccountLogin,
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  })
  const { snackbarDispatch } = useContext(SnackbarContext)

  const login = async (data: SwitchAccountLoginForm) => {
    const result = await signIn('credentials', {
      email: data.email,
      password: data.password,
      redirect: false,
    })
    // signInは認証に失敗したときもresolveされる
    // ref: https://next-auth.js.org/getting-started/client#signin
    if (result?.ok) {
      await generateMixpanelUser(data.email)
      // 旧アカウントのaccessTokenキャッシュをクリアし、次回リクエスト時に新しいセッションから再取得させる
      resetAccessTokenCache()
      // 全てのSWRキャッシュを再検証し、画面上のユーザー依存データを新しいアカウントの内容に更新する
      await mutate(() => true, undefined, { revalidate: true })
      close()
    } else {
      // ログイン失敗時は現在のアカウントのログイン状態を維持し、ダイアログも閉じない
      snackbarDispatch({ message: E0004(), severity: 'error' })
    }
  }

  return {
    formMethods,
    login,
  }
}
